import { MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";

type Props = {
  canManage: boolean;
  onViewDetails: () => void;
  onEditDetails: () => void;
  onRefresh: () => void;
  onDelete: () => void;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

// Radix portals the menu content, but React synthetic events still bubble up
// the *component* tree to the parent <li onClick={handleSelect}> — which would
// open the drawer right after our action fired. Wrap each handler so the
// menu's intent (Edit, Refresh, Delete) wins.
const withStop =
  (handler: () => void) =>
  (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
  };

const MCPItemActions = ({
  canManage,
  onViewDetails,
  onEditDetails,
  onRefresh,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={stopPropagation}
          className="flex size-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:text-foreground"
          aria-label={t("mcpPage.actions.more")}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={withStop(onViewDetails)}>
          {t("mcpPage.actions.viewDetails")}
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem onClick={withStop(onEditDetails)}>
            {t("mcpPage.actions.editDetails")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={withStop(onRefresh)}>
          {t("mcpPage.actions.refresh")}
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem variant="destructive" onClick={withStop(onDelete)}>
            {t("mcpPage.actions.delete")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MCPItemActions;
